import type {
  AoSGameState,
  CompletedLink,
  GoodsColor,
  Hex
} from '../../types';
import { debug } from '../../utils/debug';

/**
 * リンクレベルDFSで最長経路を探索（Phase 5: 商品輸送）
 *
 * 機関車レベルの範囲内で最もリンク数が多い経路を返す。
 * これにより収入が最大化される（1リンク = +1収入）。
 *
 * @param G - ゲーム状態
 * @param fromCityId - 出発都市ID
 * @param toCityId - 目的都市ID
 * @param goodsColor - 商品色（同色都市チェック用）
 * @param maxLinks - 最大リンク数（機関車レベル）
 * @returns 経路（リンクIDの配列）、または null
 *
 * @see docs/specifications/logic/common/phase_05_move_goods.md Section 4.1
 */
export function findShortestPath(
  G: AoSGameState,
  fromCityId: string,
  toCityId: string,
  goodsColor: GoodsColor,
  maxLinks: number
): string[] | null {
  // 1. 出発都市に接続しているリンクを取得
  const startLinks = G.completedLinks.filter(link =>
    link.startId === fromCityId || link.endId === fromCityId
  );

  if (startLinks.length === 0) {
    debug.log(`[pathfinder] No links connected to start city: ${fromCityId}`);
    return null; // 出発都市に接続なし
  }

  // 2. 全ての有効な経路を収集するための配列
  const validPaths: string[][] = [];

  // 3. DFSで全経路を探索
  // 初期状態: 出発都市に接続する全リンクをスタックに追加
  for (const link of startLinks) {
    const nextEnd = link.startId === fromCityId ? link.endId : link.startId;
    const visited = new Set([fromCityId, nextEnd]);

    // 中間の同色都市チェック（スタートから1リンク目）
    const endIsCity = isCityId(G, nextEnd);
    if (endIsCity) {
      const cityHex = getCityHex(G, nextEnd);
      const city = cityHex?.city || cityHex?.town?.newCity;
      if (city && cityAcceptsColor(city, goodsColor)) {
        // 同色都市に到達
        if (nextEnd === toCityId) {
          // 目的地の同色都市 → 有効な経路として記録
          validPaths.push([link.id]);
        }
        // 中間の同色都市 → この経路は使用不可
        continue;
      }
    }

    // 目的地に到達（1リンク）
    if (nextEnd === toCityId) {
      validPaths.push([link.id]);
      continue;
    }

    // 経路長制限内であれば継続探索
    if (1 < maxLinks) {
      dfs(G, {
        linkId: link.id,
        path: [link.id],
        visited: visited,
        currentEnd: nextEnd
      }, toCityId, goodsColor, maxLinks, validPaths);
    }
  }

  // 4. 有効な経路の中で最長のものを返す
  if (validPaths.length === 0) {
    debug.log(`[pathfinder] No path found from ${fromCityId} to ${toCityId}`);
    return null;
  }

  // 最長経路を選択
  let longestPath = validPaths[0];
  for (const path of validPaths) {
    if (path.length > longestPath.length) {
      longestPath = path;
    }
  }

  debug.log(`[pathfinder] Path found (longest): ${longestPath.join(' → ')} (${longestPath.length} links)`);
  return longestPath;
}

/**
 * 同一目的地への全ての有効な経路を返す（リンク数が異なる経路を全て取得）
 *
 * @param G - ゲーム状態
 * @param fromCityId - 出発都市ID
 * @param toCityId - 目的都市ID
 * @param goodsColor - 商品色（同色都市チェック用）
 * @param maxLinks - 最大リンク数（機関車レベル）
 * @returns 全ての有効な経路（リンクIDの配列の配列）
 */
export function findAllPaths(
  G: AoSGameState,
  fromCityId: string,
  toCityId: string,
  goodsColor: GoodsColor,
  maxLinks: number
): string[][] {
  // 1. 出発都市に接続しているリンクを取得
  const startLinks = G.completedLinks.filter(link =>
    link.startId === fromCityId || link.endId === fromCityId
  );

  if (startLinks.length === 0) {
    return [];
  }

  // 2. 全ての有効な経路を収集するための配列
  const validPaths: string[][] = [];

  // 3. DFSで全経路を探索
  for (const link of startLinks) {
    const nextEnd = link.startId === fromCityId ? link.endId : link.startId;
    const visited = new Set([fromCityId, nextEnd]);

    // 中間の同色都市チェック
    const endIsCity = isCityId(G, nextEnd);
    if (endIsCity) {
      const cityHex = getCityHex(G, nextEnd);
      const city = cityHex?.city || cityHex?.town?.newCity;
      if (city && cityAcceptsColor(city, goodsColor)) {
        if (nextEnd === toCityId) {
          validPaths.push([link.id]);
        }
        continue;
      }
    }

    if (nextEnd === toCityId) {
      validPaths.push([link.id]);
      continue;
    }

    if (1 < maxLinks) {
      dfs(G, {
        linkId: link.id,
        path: [link.id],
        visited: visited,
        currentEnd: nextEnd
      }, toCityId, goodsColor, maxLinks, validPaths);
    }
  }

  return validPaths;
}

/**
 * DFSヘルパー関数：再帰的に全経路を探索
 */
function dfs(
  G: AoSGameState,
  current: {
    linkId: string;
    path: string[];
    visited: Set<string>;
    currentEnd: string;
  },
  toCityId: string,
  goodsColor: GoodsColor,
  maxLinks: number,
  validPaths: string[][]
): void {
  // 隣接リンクを探索（currentEnd に実際に接続しているリンクのみ）
  const adjacentLinks = G.completedLinks.filter(link =>
    isLinkConnectedTo(link, current.currentEnd) &&
    !current.path.includes(link.id)
  );

  for (const adjLink of adjacentLinks) {
    // 次の訪問先（currentEnd から反対側の端点）
    const nextEnd = adjLink.startId === current.currentEnd
      ? adjLink.endId
      : adjLink.startId;

    // ループチェック
    if (current.visited.has(nextEnd)) {
      continue;
    }

    // 新しいパスと訪問済みセットを作成
    const newPath = [...current.path, adjLink.id];
    const newVisited = new Set(current.visited);
    newVisited.add(nextEnd);

    // 中間の同色都市チェック
    const endIsCity = isCityId(G, nextEnd);
    if (endIsCity) {
      const cityHex = getCityHex(G, nextEnd);
      const city = cityHex?.city || cityHex?.town?.newCity;
      if (city && cityAcceptsColor(city, goodsColor)) {
        // 同色都市に到達
        if (nextEnd === toCityId) {
          // 目的地の同色都市 → 有効な経路として記録
          validPaths.push(newPath);
        }
        // 中間の同色都市 → この経路は使用不可
        continue;
      }
    }

    // 目的地に到達
    if (nextEnd === toCityId) {
      validPaths.push(newPath);
      continue;
    }

    // 経路長制限内であれば継続探索
    if (newPath.length < maxLinks) {
      dfs(G, {
        linkId: adjLink.id,
        path: newPath,
        visited: newVisited,
        currentEnd: nextEnd
      }, toCityId, goodsColor, maxLinks, validPaths);
    }
  }
}

/**
 * リンクが指定した都市/町に接続しているか判定
 *
 * @param link - リンク
 * @param cityId - 都市/町ID
 * @returns 接続していれば true
 */
function isLinkConnectedTo(link: CompletedLink, cityId: string): boolean {
  return link.startId === cityId || link.endId === cityId;
}

/**
 * 2つのリンクが隣接しているか判定
 *
 * リンクAとリンクBが隣接 <=> 共通の都市/町を持つ
 *
 * @param linkA - リンクA
 * @param linkB - リンクB
 * @returns 隣接していれば true
 */
export function areLinksAdjacent(
  linkA: CompletedLink,
  linkB: CompletedLink
): boolean {
  // リンクAの両端
  const aEnds = [linkA.startId, linkA.endId];

  // リンクBの両端
  const bEnds = [linkB.startId, linkB.endId];

  // 共通の都市/町があるか
  for (const aEnd of aEnds) {
    for (const bEnd of bEnds) {
      if (aEnd === bEnd) {
        return true;
      }
    }
  }

  return false;
}

/**
 * IDが都市IDか判定（町IDでないか）
 *
 * @param G - ゲーム状態
 * @param id - 都市または町のID
 * @returns 都市IDならtrue（新都市タイルも含む）
 */
function isCityId(G: AoSGameState, id: string): boolean {
  return G.map.some(hex =>
    (hex.city?.id === id) ||
    (hex.town?.newCity?.id === id)
  );
}

/**
 * 都市IDからHexを取得
 *
 * @param G - ゲーム状態
 * @param cityId - 都市ID
 * @returns Hex（見つからない場合はnull、新都市タイルも含む）
 */
function getCityHex(G: AoSGameState, cityId: string): Hex | null {
  return G.map.find(hex =>
    (hex.city?.id === cityId) ||
    (hex.town?.newCity?.id === cityId)
  ) || null;
}

/**
 * 都市が指定した商品色を受け入れるか判定
 * color が配列の場合は複数色を受け入れる
 *
 * @param city - 都市情報
 * @param goodsColor - 商品色
 * @returns 受け入れ可能なら true
 */
function cityAcceptsColor(
  city: { color: GoodsColor | GoodsColor[] },
  goodsColor: GoodsColor
): boolean {
  const acceptedColors = Array.isArray(city.color) ? city.color : [city.color];
  return acceptedColors.includes(goodsColor);
}

/**
 * 収入計算（商品輸送時）
 *
 * @param G - ゲーム状態
 * @param path - 経路（リンクIDの配列）
 * @returns 各プレイヤーの収入増加量
 */
export function calculateIncome(
  G: AoSGameState,
  path: string[]
): Record<string, number> {
  const income: Record<string, number> = {};

  for (const linkId of path) {
    const link = G.completedLinks.find(l => l.id === linkId)!;
    const owner = link.owner;

    // 所有者なしリンク(owner:'')は収入を発生させない
    if (owner === '') continue;

    // 各リンクの所有者に +1
    income[owner] = (income[owner] || 0) + 1;
  }

  return income;
}

/**
 * 輸送時の予想収入を計算（UI表示用＆Move実行用の共通関数）
 *
 * - Gray City への輸送は収入0（Seattle Metro等）
 * - 通常の輸送は経路上のリンク数（所有者ありのリンクのみ）
 *
 * @param G - ゲーム状態
 * @param toCityId - 目的地の都市ID
 * @param path - 経路（リンクIDの配列）
 * @returns 収入増加量（合計）
 */
export function calculateExpectedIncome(
  G: AoSGameState,
  toCityId: string,
  path: string[]
): number {
  // Gray City への輸送は収入0
  const grayCityIds = G.mapConfig?.grayCityIds ?? [];
  if (grayCityIds.includes(toCityId)) {
    return 0;
  }

  // 通常: 所有者ありリンクの数 = 収入
  const incomeByOwner = calculateIncome(G, path);
  return Object.values(incomeByOwner).reduce((sum, val) => sum + val, 0);
}
